import { gql } from '@apollo/client';
import { Box, Button, useDisclosure } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
// import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '~auth/useAuth';
import { ConfigurationTable_ConfigurationFragment } from '~components/configurations/ConfigurationsTable/__generated__/ConfigurationsTable.graphql';
// import { useAuth } from '~auth/useAuth';
import {
  ConfigurationTable,
  useConfigurationTable,
} from '~components/configurations/ConfigurationsTable/ConfigurationsTable';
import { CreateConfigFromTemplateModal } from '~components/configurations/CreateConfigurationFromTemplateWizard';
import { EmptyConfigurations } from '~components/configurations/EmptyConfigurations';
import { SetDefaultConfigurationModal } from '~components/configurations/SetDefaultConfiguration';
import { DefaultConfigurationProvider } from '~components/configurations/useDefaultConfiguration';
// import { FilterButton } from '~components/displays/ClaimCodeTable/Filtering/FilterButton';
import { Page } from '~components/ui/Page';
import { PageContent } from '~components/ui/PageContent';
import { PageHeading } from '~components/ui/PageHeading';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { ConfigurationStatus, DefaultConfigurationType } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { ensure } from '~utils/types';
import { useCustomerConfigurationsPageQuery } from './__generated__/index.graphql';

const POLL_INTERVAL = 5000;

export function CustomerConfigurationsPage() {
  const { customer } = useParams();
  const { verifyUserPermissions } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const createConfigFromTemplateModal = useDisclosure();
  const setDefaultConfigurationModal = useDisclosure();

  const [configStatus] = useState<ConfigurationStatus[] | undefined>();

  const customerHandle = ensure(customer);

  const isConfigurationLimitReached = false;

  const hasConfigurationCreatePermission = verifyUserPermissions([Permission.ConfigurationCreate]);

  const hasDefaultConfigurationPermission = verifyUserPermissions([Permission.ConfigurationUpdate]);

  const canCreateConfiguration = hasConfigurationCreatePermission;

  const { data, loading, error, startPolling, stopPolling } = useCustomerConfigurationsPageQuery({
    variables: {
      customerHandle,
      pageNumber: 1,
      // pageSize: 50,
      searchTerm,
      filter: {
        status: configStatus
          ? configStatus
          : [ConfigurationStatus.Published, ConfigurationStatus.Unpublished],
      },
    },
    pollInterval: POLL_INTERVAL,
  });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const allConfigurations = data?.customer?.configurations ?? [];

  const handleCreate = createConfigFromTemplateModal.onOpen;
  const handleSetDefaultConfiguration = setDefaultConfigurationModal.onOpen;

  const table = useConfigurationTable(allConfigurations);

  const filteredConfigurations = allConfigurations.filter((config) =>
    config.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  const hasAnyConfigurations = allConfigurations.length > 0;
  const hasSearchResults = filteredConfigurations.length > 0;

  const handleConfigurationClicked = useCallback(
    (config: ConfigurationTable_ConfigurationFragment) => {
      const showPublished = config.state === ConfigurationStatus.Published && config?.draft;

      navigate(`${config.id}/config-summary`, {
        state: { showPublished },
      });
    },
    [navigate],
  );

  return (
    <Page title={t('configurations')} pageName="configurations_overview">
      <PageContent>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : (
          <>
            <PageHeading>{t('configurations')}</PageHeading>
            <PageHeading
              actions={
                <>
                  <Button
                    mr={4}
                    variant="outline"
                    onClick={handleSetDefaultConfiguration}
                    // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
                    isDisabled={!hasDefaultConfigurationPermission}
                  >
                    {t('setDefaultConfiguration')}
                  </Button>
                  <Button
                    variant="solid"
                    colorScheme="blue"
                    onClick={handleCreate}
                    // Remove once subscriptions are released to everyone: https://inthepocket.atlassian.net/browse/TPVWAVE-1460
                    isDisabled={!canCreateConfiguration}
                  >
                    + {t('newConfiguration')}
                  </Button>
                </>
              }
              marginBottom={isConfigurationLimitReached ? '8' : undefined}
            >
              {/* <FilterButton enabledFilterCount={0} /> */}
            </PageHeading>
            {(hasAnyConfigurations || searchTerm) && (
              <Box mt={6}>
                <ConfigurationTable.SearchSelector
                  search={searchTerm}
                  handleSetSearch={setSearchTerm}
                />
              </Box>
            )}
            <Box
              boxShadow="elevated"
              border="1px solid"
              borderRadius="md"
              borderColor="gray.100"
              overflowX="auto"
              background="white"
              marginTop={8}
            >
              <DefaultConfigurationProvider
                defaultConfigurations={
                  data?.customer?.customerDefaultConfigurations
                    ? [data.customer.customerDefaultConfigurations]
                    : []
                }
                multiClaimDefaultConfigurations={
                  data?.customer?.multiClaimDefaultConfigurations
                    ? data.customer.multiClaimDefaultConfigurations
                    : []
                }
              >
                {!hasAnyConfigurations && !searchTerm ? (
                  <EmptyConfigurations />
                ) : (
                  <ConfigurationTable
                    table={table}
                    onGoToDetail={handleConfigurationClicked}
                    hasSearchResults={hasSearchResults}
                  />
                )}
              </DefaultConfigurationProvider>
            </Box>
          </>
        )}
        {createConfigFromTemplateModal.isOpen && (
          <CreateConfigFromTemplateModal
            isOpen={createConfigFromTemplateModal.isOpen}
            onCancel={createConfigFromTemplateModal.onClose}
          />
        )}
        {setDefaultConfigurationModal.isOpen && (
          <SetDefaultConfigurationModal
            isOpen={setDefaultConfigurationModal.isOpen}
            onCancel={setDefaultConfigurationModal.onClose}
            configurations={data?.customer?.configurations ?? []}
            defaultConfigurationType={DefaultConfigurationType.Customer}
            defaultConfigurations={
              data?.customer?.customerDefaultConfigurations?.defaultConfigurations ?? []
            }
            supportedPlatforms={data?.customer?.supportedPlatforms ?? []}
          />
        )}
      </PageContent>
    </Page>
  );
}

CustomerConfigurationsPage.graphql = {
  queries: {
    CustomerConfigurationsPage: gql`
      query CustomerConfigurationsPage(
        $customerHandle: String!
        $pageNumber: Int!
        $searchTerm: String
        $filter: ConfigurationFilters
      ) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          supportedPlatforms
          customerDefaultConfigurations {
            id
            type
            defaultConfigurations {
              platform
              configurationId
            }
          }
          multiClaimDefaultConfigurations {
            id
            type
            defaultConfigurations {
              platform
              configurationId
            }
          }
          configurations(pageNumber: $pageNumber, searchTerm: $searchTerm, filter: $filter) {
            ...ConfigurationTable_configuration
          }
        }
      }
    `,
  },
};
